import {
    AlertTriangle,
    ArrowRight,
    BarChart3,
    Bot,
    CheckCircle2,
    ChevronRight,
    CircleDollarSign,
    Clock3,
    Landmark,
    Menu,
    MessageSquare,
    PlayCircle,
    Receipt,
    ShieldCheck,
    Sparkles,
    Star,
    Users,
    Wallet,
    X,
} from 'lucide-react'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import AuthContext from '../../../store/auth-context'
import { fetchPayrollOverview } from './payrollOverviewService'
import styles from './PayrollOverview.module.css'

const NAV_ITEMS = [
    { label: 'Features', target: 'features' },
    { label: 'Compliance', target: 'compliance' },
    { label: 'MUNIM ji', target: 'munim' },
    { label: 'Pricing', target: 'comparison' },
]

const PAIN_POINTS = [
    {
        icon: Clock3,
        title: 'Payday pe 4 ghante waste.',
        body: 'Every 1st of the month, you disappear into Excel. Counting attendance. Running formulas. Hoping nothing is wrong. Meanwhile, the salon is full of clients.',
    },
    {
        icon: MessageSquare,
        title: 'Staff ka monthly complaint.',
        body: '“Mera commission galat hai.” Every month, at least one dispute. No paper trail. No clear formula. Just an awkward conversation that erodes trust.',
    },
    {
        icon: AlertTriangle,
        title: 'PF ka darr.',
        body: 'You know you should calculate it. You are not sure if you are doing it right. Your accountant charges every time you call. And that nagging feeling does not go away.',
    },
]

const TRUST_STATS = [
    {
        value: 2400,
        suffix: '+',
        label: 'Salons running payroll on Lokaci Pro',
        note: 'From independent studios to multi-branch chains.',
    },
    {
        value: 5,
        prefix: '< ',
        label: 'Monthly payroll completion time',
        note: 'From attendance to payout review in one sitting.',
    },
    {
        value: 98.4,
        suffix: '%',
        label: 'Accuracy on first run',
        note: 'Clean numbers before the staff asks questions.',
        decimals: 1,
    },
    {
        value: 0,
        prefix: '₹',
        label: 'Calculation errors since launch',
        note: 'Because formulas should not live in somebody’s laptop.',
    },
]

const HOW_IT_WORKS = [
    {
        title: 'Ek click. Poora salon ka hisaab.',
        body: 'Mark the month. Hit Generate. Lokaci Pro reads attendance, calculates every staff member’s salary, commission, and deductions — in seconds. No formula. No lookup. Just results.',
        badge: 'Takes under 30 seconds for 20 staff',
        layout: 'left',
        preview: {
            tag: 'Generate',
            lines: ['Attendance synced', 'Salary structure applied', 'Commission rules evaluated'],
            footer: '12 staff ready · 2 warnings flagged',
        },
    },
    {
        title: 'Numbers dekho. Kuch galat lage? Fix karo.',
        body: 'DRAFT status gives you full control. Change a number, recompute one staff, override a commission — all with a full audit trail. Nothing locks until you say so.',
        badge: 'Any change tracked with who & when',
        layout: 'right',
        preview: {
            tag: 'Review',
            lines: ['Priya Sharma · Override commission', '2 warnings need review', 'Audit trail updated 2 mins ago'],
            footer: 'Draft stays editable till final confirm',
        },
    },
    {
        title: 'Lock karo. Payslips ready.',
        body: 'One confirmation. Leave balances update. Advance EMIs mark paid. All 12 payslips generated and ready to share — in the same moment.',
        badge: 'Irreversible by design. Audit-proof.',
        layout: 'left',
        preview: {
            tag: 'Finalize',
            lines: ['₹2,14,800 total payout', '12 payslips generated', 'Leave & EMI ledgers updated'],
            footer: 'Finalized successfully ✓',
        },
    },
    {
        title: 'WhatsApp pe bhejo. Done.',
        body: 'One tap sends every staff member their payslip. A secure link. No app needed. They read it on any browser. You track who opened it.',
        badge: 'Delivery status per staff member',
        layout: 'right',
        preview: {
            tag: 'Share',
            lines: ['Sent to 12 staff', '11 delivered · 8 opened', '1 dispute raised from payslip view'],
            footer: 'WhatsApp share status live',
        },
    },
]

const FEATURE_CARDS = [
    {
        icon: CircleDollarSign,
        title: 'Commission jo khud calculate ho.',
        body: 'Flat %, revenue slabs, category-wise rates, target bonuses, attendance rewards — sab configure karo ek baar. Har mahine automatic.',
        tag: 'Variable Pay',
    },
    {
        icon: ShieldCheck,
        title: 'PF. ESI. PT. Galti ki koi gunjaaish nahi.',
        body: 'Configure once. Every month — employee deductions, employer contributions, state-wise Professional Tax slabs — all calculated. CSV export ready for your accountant before the 15th.',
        tag: 'Statutory Compliance',
    },
    {
        icon: Wallet,
        title: 'Advance diya — bhool gaye ka zamaana gaya.',
        body: 'Log the advance. Set EMI. Lokaci Pro deducts automatically every month until it is fully repaid. Reschedule, foreclose, write-off — all tracked.',
        tag: 'Advance Management',
    },
    {
        icon: Users,
        title: 'Koi bhi staff jaaye — hisaab saaf hoga.',
        body: 'Enter last working date. Partial month salary, PL encashment, advance recovery — calculated in seconds. The most stressful moment in salon HR, made completely calm.',
        tag: 'Exit Management',
    },
    {
        icon: Bot,
        title: 'Raat 2 baje bhi jawab milega.',
        body: 'Payroll ka koi bhi sawaal — Hindi mein, Hinglish mein, English mein. MUNIM ji — your built-in payroll expert — available 24/7 inside the app.',
        tag: '✦ MUNIM ji',
        featured: true,
    },
    {
        icon: BarChart3,
        title: 'Salary badhaane se pehle — dekhlo kitna padega.',
        body: 'Payroll Simulation lets you model any change before applying it. Increase commission rate, add an allowance — see the monthly and annual cost impact per staff, instantly.',
        tag: 'Smart Reports',
    },
]

const COMPLIANCE_CARDS = [
    {
        icon: ShieldCheck,
        title: 'PF',
        body: 'Automatic 12% calc on basic. Ceiling enforced. Export ready.',
    },
    {
        icon: Receipt,
        title: 'ESI',
        body: 'Eligibility auto-check per staff. ₹21,000 limit. Export ready.',
    },
    {
        icon: Landmark,
        title: 'Professional Tax',
        body: 'State-wise slabs. Auto-applied. 18 states covered. Export ready.',
    },
]

const TESTIMONIALS = [
    {
        initials: 'HM',
        name: 'Hiral Mehta',
        salon: 'Glam Studio, Surat · 14 staff',
        quote: 'Pehle mahine ke end pe 3-4 ghante lagta tha. Ab 20 minute mein khatam. Staff ke disputes bhi nahi aate — payslip mein sab likha hota hai. MUNIM ji toh bonus hai.',
    },
    {
        initials: 'RT',
        name: 'Rajesh Tandon',
        salon: 'The Barber Club, Connaught Place · 8 staff',
        quote: 'Commission ka jhagda har mahine hota tha. Lokaci Pro ke baad — kabhi nahi hua. Staff khud dekh leta hai revenue aur commission. Transparency ne sab badal diya.',
    },
    {
        initials: 'SK',
        name: 'Swati Kulkarni',
        salon: 'Bliss Salon, Koregaon Park · 22 staff',
        quote: 'FnF settle karna meri sabse badi problem thi. 3 staff ek saath gaye last year. MUNIM ji ne teeno ka FnF 10 minute mein calculate kar diya. Koi confusion nahi, koi dispute nahi.',
    },
]

const COMPARISON_ROWS = [
    ['Salon-specific design', '✓', '✗', '✗'],
    ['Commission from bookings', '✓', 'Manual', '✗'],
    ['Hindi/Hinglish support', '✓', '✗', '✗'],
    ['MUNIM ji 24/7 expert', '✓', '✗', '✗'],
    ['WhatsApp payslip', '✓', '✗', 'Rare'],
    ['FnF in 5 minutes', '✓', 'Hours', 'Partial'],
    ['Advance auto-deduction', '✓', 'Manual', 'Partial'],
    ['Setup time', '2 hours', 'Ongoing', 'Days'],
    ['Price for Un-limited staff', '₹250 / month', '“Free” + time', '₹3,000 / month'],
]

const MUNIM_QUESTIONS = [
    'PF kaise calculate hota hai?',
    'Staff ne resign kiya — ab kya?',
    'Attendance pending hai — kya karein?',
]

const DEFAULT_OVERVIEW_SECTIONS = [
    'hero',
    'problemBar',
    'painPoints',
    'trustStats',
    'howItWorks',
    'features',
    'payslipShowcase',
    'compliance',
    'munimSpotlight',
    'testimonials',
    'comparison',
    'ctaCloser',
]

const NAV_SECTION_MAP = {
    features: 'features',
    compliance: 'compliance',
    munim: 'munimSpotlight',
    comparison: 'comparison',
}

const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`

const getCurrentPayrollCycle = (date = new Date()) => {
    const month = new Intl.DateTimeFormat('en-IN', { month: 'long' }).format(date)
    const year = new Intl.DateTimeFormat('en-IN', { year: 'numeric' }).format(date)

    return {
        month,
        year,
        label: `${month} ${year}`,
    }
}

const toNumber = (value, fallback = 0) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
}

const parseStatValue = (valueText = '') => {
    const text = String(valueText || '').trim()
    const hasRupee = text.includes('₹')
    const hasLessThan = text.includes('<')
    const hasPercent = text.includes('%')
    const hasPlus = text.includes('+')

    const numericRaw = text.replace(/[^0-9.]/g, '')
    const numeric = toNumber(numericRaw, 0)

    return {
        value: numeric,
        prefix: `${hasLessThan ? '< ' : ''}${hasRupee ? '₹' : ''}`,
        suffix: `${hasPercent ? '%' : ''}${hasPlus ? '+' : ''}`,
        decimals: String(numeric).includes('.') ? 1 : 0,
    }
}

const mapApiTrustStat = (item = {}) => {
    const parsedFromText = parseStatValue(item?.value)
    const animatedValue =
        toNumber(item?.animateTo, NaN) ||
        toNumber(item?.rawValue, NaN) ||
        parsedFromText.value

    return {
        value: Number.isFinite(animatedValue) ? animatedValue : 0,
        prefix: parsedFromText.prefix,
        suffix: parsedFromText.suffix,
        decimals: parsedFromText.decimals,
        label: item?.label || 'Stat',
        note: item?.sublabel || '',
    }
}

const parseJson = (value) => {
    try {
        return value ? JSON.parse(value) : null
    } catch {
        return null
    }
}

const useReveal = () => {
    const ref = useRef(null)
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        const node = ref.current
        if (!node) return undefined

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true)
                    observer.disconnect()
                }
            },
            { threshold: 0.18 }
        )

        observer.observe(node)
        return () => observer.disconnect()
    }, [])

    return [ref, visible]
}

const Reveal = ({ children, className = '', delay = 0 }) => {
    const [ref, visible] = useReveal()

    return (
        <div
            ref={ref}
            className={`${styles.reveal} ${visible ? styles.revealVisible : ''} ${className}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            {children}
        </div>
    )
}

const AnimatedCounter = ({ value, prefix = '', suffix = '', decimals = 0, label, note }) => {
    const [ref, visible] = useReveal()
    const [count, setCount] = useState(0)

    useEffect(() => {
        if (!visible) return undefined

        const duration = 1400
        const start = performance.now()
        let frameId = null

        const tick = (now) => {
            const progress = Math.min((now - start) / duration, 1)
            const eased = 1 - Math.pow(1 - progress, 3)
            setCount(value * eased)
            if (progress < 1) {
                frameId = window.requestAnimationFrame(tick)
            }
        }

        frameId = window.requestAnimationFrame(tick)
        return () => window.cancelAnimationFrame(frameId)
    }, [value, visible])

    const displayValue = `${prefix}${count.toLocaleString('en-IN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    })}${suffix}`

    return (
        <div ref={ref} className={styles.statCard}>
            <strong className={styles.statValue}>{visible ? displayValue : `${prefix}0${suffix}`}</strong>
            <p className={styles.statLabel}>{label}</p>
            <span className={styles.statNote}>{note}</span>
        </div>
    )
}

const TypewriterDemo = () => {
    const [activeIndex, setActiveIndex] = useState(0)
    const [charIndex, setCharIndex] = useState(0)

    useEffect(() => {
        const activeText = MUNIM_QUESTIONS[activeIndex]
        const isComplete = charIndex >= activeText.length

        const timer = window.setTimeout(
            () => {
                if (!isComplete) {
                    setCharIndex((prev) => prev + 1)
                    return
                }

                setCharIndex(0)
                setActiveIndex((prev) => (prev + 1) % MUNIM_QUESTIONS.length)
            },
            isComplete ? 1500 : 45
        )

        return () => window.clearTimeout(timer)
    }, [activeIndex, charIndex])

    const currentQuestion = MUNIM_QUESTIONS[activeIndex].slice(0, charIndex)

    return (
        <div className={styles.typewriterWrap}>
            {MUNIM_QUESTIONS.map((question, index) => (
                <div
                    key={question}
                    className={`${styles.typewriterRow} ${index === activeIndex ? styles.typewriterRowActive : ''}`}
                >
                    <span className={styles.typewriterQuestion}>
                        {index === activeIndex ? `${currentQuestion}${charIndex % 2 === 0 ? '|' : ''}` : question}
                    </span>
                    <span className={styles.typewriterAnswer}>Answer ready in seconds</span>
                </div>
            ))}
        </div>
    )
}

const PayrollOverview = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)

    const [menuOpen, setMenuOpen] = useState(false)
    const [overviewData, setOverviewData] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const activeStore = useMemo(() => parseJson(asCtx?.activeStore), [asCtx?.activeStore])
    const loginData = useMemo(() => parseJson(authCtx?.loginData), [authCtx?.loginData])
    const currentPayrollCycle = useMemo(() => getCurrentPayrollCycle(), [])

    useEffect(() => {
        let isMounted = true

        const loadOverview = async () => {
            try {
                const response = await fetchPayrollOverview({
                    token: authCtx?.token || null,
                    storeIdentifier: activeStore?.storeIdentifier || null,
                })

                if (!isMounted) return
                setOverviewData(response)
            } catch (error) {
                if (!isMounted) return

                if (error?.message === 'tokenExpired') {
                    authCtx.logout()
                    return
                }

                setOverviewData(null)
            } finally {
                if (isMounted) setIsLoading(false)
            }
        }

        loadOverview()

        return () => {
            isMounted = false
        }
    }, [activeStore?.storeIdentifier, authCtx])

    const apiMeta = overviewData?.meta || {}
    const apiHero = overviewData?.hero || {}
    const overviewSections = Array.isArray(overviewData?.sectionsToShow) && overviewData.sectionsToShow.length > 0
        ? overviewData.sectionsToShow
        : DEFAULT_OVERVIEW_SECTIONS
    const showSection = (sectionKey) => overviewSections.includes(sectionKey)

    const visibleNavItems = useMemo(
        () => NAV_ITEMS.filter((item) => showSection(NAV_SECTION_MAP[item.target])),
        [overviewSections]
    )

    const isEnrolled = Boolean(apiMeta?.isEnrolled ?? loginData?.payrollEnrolled ?? false)

    const fallbackRunSummary = useMemo(
        () => ({
            cycleLabel: currentPayrollCycle.label,
            runStatus: 'DRAFT',
            totalStaffCount: 12,
            totalPayout: 214800,
            warningsCount: 2,
            pendingAttendanceCount: 4,
            generatedAgo: 'Generated 3 days ago',
            runIdentifier: 'PR-APR-2026-014',
            lastActivity: '3 days ago',
        }),
        [currentPayrollCycle.label]
    )

    const apiRunSummary = useMemo(() => {
        if (!isEnrolled) return null

        const activeRun = apiHero?.activeRun || {}
        if (!activeRun?.runIdentifier && !activeRun?.cycleLabel) return null

        return {
            cycleLabel: activeRun?.cycleLabel || currentPayrollCycle.label,
            runStatus: activeRun?.runStatus || 'DRAFT',
            totalStaffCount: toNumber(activeRun?.staffCount, 12),
            totalPayout: toNumber(activeRun?.totalNetPayout, 0),
            warningsCount: toNumber(activeRun?.warningCount, 0),
            pendingAttendanceCount: toNumber(activeRun?.pendingAttendance, 0),
            generatedAgo: apiHero?.runStatus?.message || 'Generated recently',
            runIdentifier: activeRun?.runIdentifier || '',
            lastActivity: activeRun?.lastActivityLabel || 'Recently',
        }
    }, [apiHero?.activeRun, apiHero?.runStatus?.message, currentPayrollCycle.label, isEnrolled])

    const currentRunSummary =
        location.state?.currentRunSummary ||
        (isEnrolled ? apiRunSummary || fallbackRunSummary : null)
    const hasActiveRun = Boolean(apiHero?.hasActiveRun ?? (currentRunSummary?.runStatus === 'DRAFT'))

    const quickActions = Array.isArray(apiHero?.quickActions) ? apiHero.quickActions : []
    const primaryQuickAction = quickActions[0] || null
    const secondaryQuickAction = quickActions[1] || null
    const munimQuickAction = quickActions.find((item) => String(item?.label || '').toLowerCase().includes('munim')) || null

    const trustStatsToRender = useMemo(() => {
        if (Array.isArray(overviewData?.trustStats) && overviewData.trustStats.length > 0) {
            return overviewData.trustStats.map(mapApiTrustStat)
        }
        return TRUST_STATS
    }, [overviewData?.trustStats])

    const storeName =
        activeStore?.storeName ||
        activeStore?.businessName ||
        loginData?.storeName ||
        'Looks Salon, Delhi'

    const scrollToSection = (target) => {
        const node = document.getElementById(target)
        if (node) {
            node.scrollIntoView({ behavior: 'smooth', block: 'start' })
            setMenuOpen(false)
        }
    }

    const handlePrimaryCta = () => {
        if (isEnrolled) {
            if (primaryQuickAction?.route) {
                navigate(primaryQuickAction.route)
                return
            }
            navigate('/payroll/run')
            return
        }

        if (apiHero?.primaryCTA?.route) {
            navigate(apiHero.primaryCTA.route)
            return
        }

        navigate('/payroll/setup')
    }

    const handleSecondaryCta = () => {
        if (isEnrolled && secondaryQuickAction?.route) {
            navigate(secondaryQuickAction.route)
            return
        }

        if (isEnrolled && hasActiveRun && currentRunSummary?.runIdentifier) {
            navigate(`/payroll/run/${currentRunSummary.runIdentifier}`)
            return
        }
        scrollToSection('how-it-works')
    }

    return (
        <div className={styles.pageShell}>
            <header className={styles.stickyHeader}>
                <div className={styles.headerBrand}>Lokaci Pro Payroll</div>

                <div className={`${styles.headerLinks} ${menuOpen ? styles.headerLinksOpen : ''}`}>
                    {!isEnrolled && visibleNavItems.map((item) => (
                        <button
                            key={item.target}
                            type="button"
                            className={styles.headerLinkBtn}
                            onClick={() => scrollToSection(item.target)}
                        >
                            {item.label}
                        </button>
                    ))}

                    {isEnrolled ? (
                        <>
                            <button type="button" className={styles.headerGhostBtn} onClick={() => navigate('/payroll/ask-munim-ji')}>
                                Ask MUNIM ji
                            </button>
                            <button type="button" className={styles.headerPrimaryBtn} onClick={() => navigate('/payroll/run')}>
                                Run Payroll <ArrowRight size={15} />
                            </button>
                        </>
                    ) : (
                        <button type="button" className={styles.headerPrimaryBtn} onClick={handlePrimaryCta}>
                            Start Now <ArrowRight size={15} />
                        </button>
                    )}
                </div>

                <button type="button" className={styles.mobileMenuBtn} onClick={() => setMenuOpen((prev) => !prev)}>
                    {menuOpen ? <X size={18} /> : <Menu size={18} />}
                </button>
            </header>

            {showSection('hero') && <section className={styles.heroSection}>
                <div className={styles.heroGrid}>
                    <div className={styles.heroCopy}>
                        {isLoading ? (
                            <>
                                <div className={`${styles.skeletonLine} ${styles.skeletonShort}`} style={{ marginBottom: 20 }} />
                                <div className={`${styles.skeletonLine} ${styles.skeletonFull}`} />
                                <div className={`${styles.skeletonLine} ${styles.skeletonMid}`} />
                                <div className={`${styles.skeletonLine} ${styles.skeletonShort}`} style={{ marginBottom: 24 }} />
                                <div className={styles.skeletonCtaRow}>
                                    <div className={`${styles.skeletonBlock} ${styles.skeletonBtn}`} />
                                    <div className={`${styles.skeletonBlock} ${styles.skeletonBtnGhost}`} />
                                </div>
                                <div className={`${styles.skeletonLine} ${styles.skeletonMid}`} style={{ marginTop: 20 }} />
                            </>
                        ) : (
                            <>
                                <span className={styles.heroEyebrow}>
                                    <Sparkles size={13} /> {isEnrolled ? (apiHero?.greeting || `Welcome back, ${storeName}`) : (apiHero?.eyebrow || 'Built for Indian Salons')}
                                </span>

                                <h1 className={styles.heroTitle}>
                                    {isEnrolled ? (
                                        <>
                                            {currentRunSummary?.cycleLabel || currentPayrollCycle.label} payroll ready
                                            <br />
                                            in 5 minutes.
                                        </>
                                    ) : (
                                        <>
                                            Payroll jo
                                            <br />
                                            khud calculate
                                            <br />
                                            kare.
                                        </>
                                    )}
                                </h1>

                                <p className={styles.heroSubtitle}>
                                    {apiHero?.subheadline || (isEnrolled
                                        ? 'Attendance se payout tak sab lined up hai. Bas review karo, finalize karo, aur team ko bhej do.'
                                        : 'Attendance se salary tak. Commission se payslip tak. Sab kuch automatic — 5 minute mein.')}
                                </p>

                                <div className={styles.heroCtaRow}>
                                    <button type="button" className={styles.primaryBtn} onClick={handlePrimaryCta}>
                                        {isEnrolled
                                            ? (primaryQuickAction?.label || `Run ${currentPayrollCycle.month} Payroll`)
                                            : (apiHero?.primaryCTA?.label || 'Start Free Trial')}
                                        <ArrowRight size={16} />
                                    </button>

                                    {isEnrolled ? (
                                        <>
                                            <button type="button" className={styles.ghostBtn} onClick={handleSecondaryCta}>
                                                {secondaryQuickAction?.label || 'View Last Run'}
                                            </button>
                                            <button
                                                type="button"
                                                className={styles.ghostBtn}
                                                onClick={() => navigate(munimQuickAction?.route || '/payroll/ask-munim-ji')}
                                            >
                                                {munimQuickAction?.label || 'Ask MUNIM ji'}
                                            </button>
                                        </>
                                    ) : (
                                        <button type="button" className={styles.ghostBtn} onClick={handleSecondaryCta}>
                                            {apiHero?.secondaryCTA?.label || 'Watch How It Works'} <PlayCircle size={16} />
                                        </button>
                                    )}
                                </div>

                                <p className={styles.heroMeta}>
                                    {isEnrolled ? 'Draft payroll stays editable till you finalize.' : (apiHero?.trustLine || 'No credit card required · Setup in under 2 hours')}
                                </p>

                                <div className={styles.heroProof}>
                                    <span className={styles.stars}><Star size={14} fill="currentColor" /> ★★★★★</span>
                                    <span>{isEnrolled ? `${currentRunSummary?.totalStaffCount || 12} staff ready for payout this cycle` : (apiHero?.socialProof || 'Used by 2,400+ salons across India')}</span>
                                </div>
                            </>
                        )}
                    </div>

                    <div className={styles.heroVisualWrap}>
                        {isLoading ? (
                            <div className={styles.skeletonVisualCard}>
                                <div className={`${styles.skeletonLine} ${styles.skeletonShort}`} style={{ marginBottom: 12 }} />
                                <div className={`${styles.skeletonBlock} ${styles.skeletonVisualAmount}`} />
                                <div className={styles.skeletonBarGroup}>
                                    <div className={`${styles.skeletonBar} ${styles.skeletonBarW72}`} />
                                    <div className={`${styles.skeletonBar} ${styles.skeletonBarW56}`} />
                                    <div className={`${styles.skeletonBar} ${styles.skeletonBarW44}`} />
                                </div>
                                <div className={`${styles.skeletonLine} ${styles.skeletonMid}`} style={{ marginTop: 12 }} />
                            </div>
                        ) : !isEnrolled ? (
                            <div className={styles.heroCardFloat}>
                                <div className={styles.floatingPayslipCard}>
                                    <div className={styles.payslipHeader}>
                                        <div>
                                            <span className={styles.mutedTiny}>Payslip Preview</span>
                                            <h3>Priya Sharma — Senior Stylist</h3>
                                        </div>
                                        <span className={styles.payslipStatus}>Sent on WhatsApp ✓</span>
                                    </div>

                                    <div className={styles.netPayBlock}>
                                        <span>Net Pay</span>
                                        <strong>{formatCurrency(18240)}</strong>
                                    </div>

                                    <div className={styles.breakdownList}>
                                        {[
                                            ['Basic', 72],
                                            ['Commission', 56],
                                            ['Attendance Bonus', 38],
                                        ].map(([label, width]) => (
                                            <div key={label} className={styles.breakdownRow}>
                                                <span>{label}</span>
                                                <div className={styles.barTrack}>
                                                    <div className={styles.barFill} style={{ width: `${width}%` }} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className={styles.floatingFooter}>
                                        <span>22 days present</span>
                                        <span>Commission auto-calculated</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className={styles.statusCardWrap}>
                                <div className={styles.statusCard}>
                                    <div className={styles.statusHeader}>
                                        <span>{currentRunSummary?.cycleLabel || currentPayrollCycle.label} Status</span>
                                        <span
                                            className={styles.statusBadge}
                                            style={{
                                                color: apiHero?.runStatus?.color || undefined,
                                                background: apiHero?.runStatus?.bg || undefined,
                                            }}
                                        >
                                            {apiHero?.runStatus?.label || currentRunSummary?.runStatus || 'DRAFT'}
                                        </span>
                                    </div>
                                    <p className={styles.statusGenerated}>{currentRunSummary?.generatedAgo || 'Generated 3 days ago'}</p>
                                    <div className={styles.statusMetrics}>
                                        <div>
                                            <strong>{currentRunSummary?.totalStaffCount || 12} staff</strong>
                                            <span>{formatCurrency(currentRunSummary?.totalPayout || 214800)} total payout</span>
                                        </div>
                                        <div>
                                            <strong>{currentRunSummary?.warningsCount || 2} warnings</strong>
                                            <span>{currentRunSummary?.pendingAttendanceCount || 4} attendance pending</span>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.inlineAction}
                                        onClick={() => navigate(currentRunSummary?.runIdentifier ? `/payroll/run` : '/payroll/run')}
                                    >
                                        Review <ChevronRight size={15} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>}

            {showSection('problemBar') && <section className={styles.problemBar}>
                <p>Agar aap abhi bhi Excel mein salary calculate karte ho — yeh page aapke liye hi hai.</p>
            </section>}

            {showSection('painPoints') && <section id="pain" className={styles.sectionLight}>
                <div className={styles.sectionContainer}>
                    <Reveal className={styles.sectionIntro}>
                        <span className={styles.sectionKicker}>Hum jaante hain.</span>
                        <h2 className={styles.sectionTitle}>Har salon owner ne yeh feel kiya hai.</h2>
                    </Reveal>

                    <div className={styles.painGrid}>
                        {PAIN_POINTS.map((item, index) => {
                            const Icon = item.icon
                            return (
                                <Reveal key={item.title} delay={index * 120} className={styles.painCard}>
                                    <div className={styles.painIcon}><Icon size={20} /></div>
                                    <h3>{item.title}</h3>
                                    <p>{item.body}</p>
                                </Reveal>
                            )
                        })}
                    </div>

                    <Reveal className={styles.transitionLine}>
                        Lokaci Pro Payroll ne yeh sab khatam kar diya. For 2,400+ salons. Ab aapki baari.
                    </Reveal>
                </div>
            </section>}

            {showSection('trustStats') && <section className={styles.sectionMuted}>
                <div className={styles.sectionContainer}>
                    <div className={styles.statsGrid}>
                        {isLoading
                            ? Array.from({ length: 4 }).map((_, i) => (
                                <div key={i} className={styles.skeletonStatCard}>
                                    <div className={`${styles.skeletonBlock} ${styles.skeletonStatValue}`} />
                                    <div className={`${styles.skeletonLine} ${styles.skeletonMid}`} style={{ marginTop: 8 }} />
                                    <div className={`${styles.skeletonLine} ${styles.skeletonShort}`} style={{ marginTop: 6 }} />
                                </div>
                            ))
                            : trustStatsToRender.map((item) => (
                                <AnimatedCounter key={item.label} {...item} />
                            ))
                        }
                    </div>
                </div>
            </section>}

            {showSection('howItWorks') && <section id="how-it-works" className={styles.sectionLight}>
                <div className={styles.sectionContainer}>
                    <Reveal className={styles.sectionIntroLeft}>
                        <span className={styles.sectionKicker}>How it works</span>
                        <h2 className={styles.sectionTitle}>Payroll run karna itna simple kabhi nahi tha.</h2>
                    </Reveal>

                    <div className={styles.stepStack}>
                        {HOW_IT_WORKS.map((step, index) => (
                            <Reveal key={step.title} className={`${styles.stepRow} ${step.layout === 'right' ? styles.stepRowReverse : ''}`}>
                                <div className={styles.stepVisual}>
                                    <div className={styles.stepVisualCard}>
                                        <span className={styles.stepVisualTag}>{step.preview.tag}</span>
                                        {step.preview.lines.map((line) => (
                                            <div key={line} className={styles.stepVisualLine}>{line}</div>
                                        ))}
                                        <div className={styles.stepVisualFooter}>{step.preview.footer}</div>
                                    </div>
                                </div>

                                <div className={styles.stepCopy}>
                                    <span className={styles.stepNumber}>0{index + 1}</span>
                                    <h3>{step.title}</h3>
                                    <p>{step.body}</p>
                                    <span className={styles.stepBadge}>← {step.badge}</span>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>}

            {showSection('features') && <section id="features" className={styles.sectionDark}>
                <div className={styles.sectionContainer}>
                    <Reveal className={styles.sectionIntroLeftDark}>
                        <span className={styles.sectionKickerAmber}>Salary ke siwa bhi bahut kuch.</span>
                        <h2 className={styles.sectionTitleLight}>Payroll sirf calculate karna nahi hota.</h2>
                        <p className={styles.sectionTextLight}>Lokaci Pro woh sab karta hai jo aapko raat ko neend aane deta hai.</p>
                    </Reveal>

                    <div className={styles.featureGrid}>
                        {FEATURE_CARDS.map((item, index) => {
                            const Icon = item.icon
                            return (
                                <Reveal
                                    key={item.title}
                                    delay={index * 80}
                                    className={`${styles.featureCard} ${item.featured ? styles.featureCardFeatured : ''}`}
                                >
                                    <div className={styles.featureCardHead}>
                                        <div className={styles.featureIcon}><Icon size={20} /></div>
                                        <span className={`${styles.featureTag} ${item.featured ? styles.featureTagFeatured : ''}`}>{item.tag}</span>
                                    </div>
                                    <h3>{item.title}</h3>
                                    <p>{item.body}</p>
                                </Reveal>
                            )
                        })}
                    </div>
                </div>
            </section>}

            {showSection('payslipShowcase') && <section className={styles.sectionLight}>
                <div className={styles.sectionContainer}>
                    <div className={styles.showcaseGrid}>
                        <Reveal className={styles.phoneMockWrap}>
                            <div className={styles.phoneFrame}>
                                <div className={styles.phoneNotch} />
                                <div className={styles.phoneScreen}>
                                    <div className={styles.phoneHeader}>Payslip · {currentPayrollCycle.label}</div>
                                    <div className={styles.phoneAmount}>{formatCurrency(18240)}</div>
                                    <div className={styles.phoneEquation}>Basic ₹12,000 × (22 ÷ 26 working days) = ₹10,153.85</div>
                                    <div className={styles.phoneRows}>
                                        {['Basic Salary', 'Service Commission', 'Advance EMI', 'PF Deduction'].map((item, index) => (
                                            <div key={item} className={styles.phoneRow}>
                                                <span>{item}</span>
                                                <strong>{index < 2 ? '+' : '-'} ₹{[10153, 5400, 1500, 980][index]}</strong>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className={styles.whatsappToast}>WhatsApp payslip delivered ✓</div>
                        </Reveal>

                        <Reveal className={styles.showcaseCopy}>
                            <span className={styles.sectionKicker}>Payslip clarity</span>
                            <h2 className={styles.sectionTitle}>Payslip jo staff ko samajh aaye.</h2>
                            <p className={styles.sectionText}>No jargon. No mystery. Every number has its calculation shown clearly. Staff knows exactly why they got what they got. Disputes reduce. Trust increases.</p>
                            <div className={styles.checkList}>
                                {[
                                    'WhatsApp delivery in one tap',
                                    'Secure link — no app needed for staff',
                                    'Delivery tracking (sent / delivered / opened)',
                                    'Dispute mechanism built in',
                                    'PF, advance, leave — all shown clearly',
                                ].map((item) => (
                                    <div key={item} className={styles.checkItem}><CheckCircle2 size={16} /> {item}</div>
                                ))}
                            </div>
                            <button type="button" className={styles.inlinePrimary} onClick={() => navigate('/payroll/run-history')}>
                                See Sample Payslip <ArrowRight size={15} />
                            </button>
                        </Reveal>
                    </div>
                </div>
            </section>}

            {showSection('compliance') && <section id="compliance" className={styles.sectionBlue}>
                <div className={styles.sectionContainer}>
                    <Reveal className={styles.sectionIntroLeft}>
                        <span className={styles.sectionKickerBlue}>Statutory Compliance</span>
                        <h2 className={styles.sectionTitle}>Government ke rules. Aapka tension nahi.</h2>
                    </Reveal>

                    <div className={styles.complianceGrid}>
                        {COMPLIANCE_CARDS.map((item, index) => {
                            const Icon = item.icon
                            return (
                                <Reveal key={item.title} delay={index * 90} className={styles.complianceCard}>
                                    <div className={styles.complianceIcon}><Icon size={20} /></div>
                                    <h3>{item.title}</h3>
                                    <p>{item.body}</p>
                                </Reveal>
                            )
                        })}
                    </div>

                    <Reveal className={styles.complianceFooter}>
                        <p>Employer contributions shown separately. One CSV export. Your accountant’s job — simplified.</p>
                        <span>Lokaci Pro calculates. Your accountant files. That is the right division of work.</span>
                    </Reveal>
                </div>
            </section>}

            {showSection('munimSpotlight') && <section id="munim" className={styles.sectionMunim}>
                <div className={styles.sectionContainerNarrow}>
                    <Reveal className={styles.munimAvatarWrap}>
                        <div className={styles.munimAvatar}>M</div>
                    </Reveal>
                    <Reveal className={styles.munimCopy}>
                        <span className={styles.sectionKickerAmber}>✦ MEET MUNIM ji</span>
                        <h2 className={styles.sectionTitleLight}>Aapka 24/7 payroll expert — app ke andar hi.</h2>
                        <p className={styles.sectionTextLight}>Master Utility for Numbers, Insights &amp; Management. Pehle Munim register mein likhta tha. Ab MUNIM ji aapke phone mein hai.</p>
                    </Reveal>
                    <Reveal className={styles.munimDemo}>
                        <TypewriterDemo />
                    </Reveal>
                    <Reveal className={styles.munimActions}>
                        <button type="button" className={styles.amberBtn} onClick={() => navigate('/payroll/ask-munim-ji')}>
                            Ask MUNIM ji Now <ArrowRight size={16} />
                        </button>
                        <p className={styles.munimMeta}>Hindi · Hinglish · English — teeno mein · No appointment · No waiting · No extra charge</p>
                    </Reveal>
                </div>
            </section>}

            {showSection('testimonials') && <section className={styles.sectionLight}>
                <div className={styles.sectionContainer}>
                    <Reveal className={styles.sectionIntroLeft}>
                        <span className={styles.sectionKicker}>Testimonials</span>
                        <h2 className={styles.sectionTitle}>2,400+ salon owners phir kabhi Excel nahi kholenge.</h2>
                    </Reveal>
                    <div className={styles.testimonialRail}>
                        {TESTIMONIALS.map((item, index) => (
                            <Reveal key={item.name} delay={index * 90} className={styles.testimonialCard}>
                                <div className={styles.testimonialHead}>
                                    <div className={styles.avatarBadge}>{item.initials}</div>
                                    <div>
                                        <h3>{item.name}</h3>
                                        <span>{item.salon}</span>
                                    </div>
                                </div>
                                <p>{item.quote}</p>
                                <div className={styles.ratingRow}>★★★★★</div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>}

            {showSection('comparison') && <section id="comparison" className={styles.sectionMuted}>
                <div className={styles.sectionContainer}>
                    <Reveal className={styles.sectionIntroLeft}>
                        <span className={styles.sectionKicker}>Comparison</span>
                        <h2 className={styles.sectionTitle}>Kyun Lokaci Pro? Comparison dekhlo.</h2>
                    </Reveal>

                    <Reveal className={styles.comparisonWrap}>
                        <div className={styles.comparisonHeader}>
                            <span>Feature</span>
                            <span>Lokaci Pro</span>
                            <span>Excel / Manual</span>
                            <span>Generic HR SW</span>
                        </div>
                        {COMPARISON_ROWS.map((row, index) => (
                            <div key={row[0]} className={styles.comparisonRow} style={{ transitionDelay: `${index * 70}ms` }}>
                                <span>{row[0]}</span>
                                <strong>{row[1]}</strong>
                                <span>{row[2]}</span>
                                <span>{row[3]}</span>
                            </div>
                        ))}
                    </Reveal>

                    <Reveal className={styles.comparisonNote}>
                        Generic HR software was not built for salons. Lokaci Pro was.
                    </Reveal>
                </div>
            </section>}

            {showSection('ctaCloser') && <section className={styles.sectionCloser}>
                <div className={styles.sectionContainerNarrow}>
                    <Reveal className={styles.closerContent}>
                        <span className={styles.sectionKickerAmber}>THE LAST PAYROLL DECISION YOU’LL EVER STRESS ABOUT</span>
                        <h2 className={styles.closerTitle}>
                            {isEnrolled ? (
                                <>
                                    {currentRunSummary?.cycleLabel || currentPayrollCycle.month} ka payroll
                                    <br />
                                    abhi bhi pending hai.
                                </>
                            ) : (
                                <>
                                    Aaj shuru karo.
                                    <br />
                                    Agla payday —
                                    <br />
                                    5 minute ka kaam.
                                </>
                            )}
                        </h2>
                        <p className={styles.closerText}>
                            {isEnrolled ? `Last activity: ${currentRunSummary?.lastActivity || '3 days ago'} · ${currentRunSummary?.pendingAttendanceCount || 4} attendance records pending` : '2-hour setup. Lifetime of calm paydays.'}
                        </p>
                        <div className={styles.closerActions}>
                            <button type="button" className={styles.amberBtnLarge} onClick={handlePrimaryCta}>
                                {isEnrolled ? `Run ${currentPayrollCycle.month} Payroll Now` : 'Speak to an Expert'} <ArrowRight size={17} />
                            </button>
                            <button
                                type="button"
                                className={styles.ghostBtnLight}
                                onClick={() => (isEnrolled ? navigate('/payroll/ask-munim-ji') : navigate('/payroll/schedule-demo'))}
                            >
                                {isEnrolled ? 'Ask MUNIM ji' : 'Schedule a Demo'}
                            </button>
                        </div>
                        <div className={styles.closerTrustRow}>
                            <span>🔒 Secure</span>
                            <span>🇮🇳 Indian servers</span>
                            <span>📞 Setup support included</span>
                        </div>
                        {!isEnrolled && <span className={styles.closerFinePrint}>Join 2,400+ salons who switched from Excel</span>}
                    </Reveal>
                </div>
            </section>}
        </div>
    )
}

export default PayrollOverview